import cn, { clsx } from "clsx";
import { dateFormatterWithTime } from "@/helpers/dateFormatter";
import UnivoLink from "@/components/common/UnivoLink";
import UnivoImage from "@/components/common/UnivoImage";
import ArrowSVG from "@/SvgIcons/ArrowDown";
import styles from "../../styles.module.scss";
import type { ContentBodyViewModel } from "../model/contentBody.types";
import strapiSingleAttribute from "@/api/strapiSingleAttribute";

function AuthorDetails({ data }: { data?: any }) {
  // v5 returns the author relation flat; strapiSingleAttribute still unwraps
  // any v4-shaped payload coming from cache.
  const attributes = strapiSingleAttribute(data);
  if (!attributes?.name) return null;

  const authorDescription = attributes.shortBio ||
    (attributes.authorBio?.length > 500
      ? `${attributes.authorBio.slice(0, 500)}...`
      : attributes.authorBio || "");
  const picture = strapiSingleAttribute(attributes.Picture);

  return (
    <div className={cn(styles.authorTile, "mt-4 mb-5")}>
      <h2 className={styles.authorTileHeading}>ABOUT AUTHOR</h2>
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          {picture?.url ? (
            <UnivoImage alt="Author" src={picture.url} className={styles.authorImage} width={48} height={48} sizes="48px" loading="lazy" fetchPriority="low" />
          ) : null}
          <div className={styles.authorNameAndSocialContainer}>
            <div className="flex flex-col justify-center text-left">
              <UnivoLink className={styles.authorName} href={attributes.slug ? `/author/${attributes.slug}` : "#"} prefetch={false}>
                {attributes.name}
              </UnivoLink>
              <h3 className={styles.authorDesignation}>{attributes.Designation}</h3>
            </div>
            {attributes.AssociatedSocialMedia?.length > 0 ? (
              <div className={styles.socialMediaIconsContainer}>
                {attributes.AssociatedSocialMedia.map((item: any, index: number) => {
                  const image = strapiSingleAttribute(item?.Image);
                  return (
                    <UnivoLink key={index} href={item?.Link || "#"} external={/^https?:\/\//i.test(item?.Link || "") || /^(mailto|tel):/i.test(item?.Link || "")} prefetch={false} className="w-5">
                      {image?.url ? <UnivoImage src={image.url} alt={image.alternativeText || image.name || "Social Icon"} className={styles.socialIcon} width={20} height={20} sizes="20px" loading="lazy" fetchPriority="low" /> : null}
                    </UnivoLink>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {authorDescription ? <div className={styles.authorDescription} dangerouslySetInnerHTML={{ __html: authorDescription }} /> : null}
    </div>
  );
}

function Toc({ viewModel }: { viewModel: ContentBodyViewModel }) {
  const { tocItems, isTocOpen, activeTocIndex, toggleToc, selectTocItem } = viewModel;
  if (!tocItems.length) return null;

  return (
    <div className={clsx(styles.tableListContainer, "max-h-50 overflow-auto")}>
      <h2 className={cn("lg:my-2 lg:font-bold text-center sm:uppercase", styles.tableHeading)} onClick={toggleToc}>
        <span>Table of Content</span>
        <span className={styles.tocArrow}><ArrowSVG rotate={isTocOpen ? 180 : 0} width={15} height={15} /></span>
      </h2>
      <ul className={cn(styles.tocList, { [styles.tocListOpen]: isTocOpen })}>
        {tocItems.map((item, index) => (
          <li key={item.anchorId} className={cn("lg:mb-2 sm:mb-1", { [styles.tocItemActive]: activeTocIndex === index })}>
            <h3>
              <UnivoLink prefetch={false} href={`#${item.anchorId}`} onClick={(event) => { event.preventDefault(); selectTocItem(item, index); }} className={cn("text-[0.6rem] leading-1 block", { "text-[#1a325d] font-bold": activeTocIndex === index })}>
                {item.title}
              </UnivoLink>
            </h3>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ContentBodyView(viewModel: ContentBodyViewModel) {
  return (
    <>
      <div className={clsx(styles.tableContentWrapper, "px-3 py-6 hidden lg:block")}><Toc viewModel={viewModel} /></div>
      <div className={cn(styles.root, "p-6")}>
        <div>
          <h1><strong id="card-details-heading">{viewModel.heading}</strong></h1>
          <p className={cn(styles.blogCreateDate, "text-right my-3")}>Updated: {dateFormatterWithTime(viewModel.updatedAt, true)} </p>
          <div className={clsx(styles.tableContentWrapper, "lg:hidden")}><Toc viewModel={viewModel} /></div>
          <div className={cn(styles.blog, "mt-5")}>
            <div ref={viewModel.contentRef} dangerouslySetInnerHTML={{ __html: viewModel.processedHtml }} />
          </div>
        </div>
        <AuthorDetails data={viewModel.author} />
      </div>
    </>
  );
}
